import React from 'react';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';

interface PrivacyProps {
  onNavigate: (page: string) => void;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-lg font-bold text-[#F7931A] uppercase tracking-wider mb-4">{title}</h2>
    <div className="text-gray-400 leading-relaxed space-y-3 text-sm">{children}</div>
  </section>
);

const Privacy: React.FC<PrivacyProps> = ({ onNavigate }) => {
  useSEO({
    title: 'Privacy Policy',
    description: 'Privacy Policy for UTXOMacro — how we handle your data, newsletter subscriptions, and contact form submissions.',
    url: 'https://utxomacro.com/privacy',
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0A0A0A' }}>
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
            <div className="w-12 h-0.5 bg-[#F7931A] rounded mb-4" />
            <p className="text-gray-600 text-sm">Last updated: August 2026</p>
          </div>

          <div className="text-gray-400 text-sm leading-relaxed mb-10 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
            UTXOMacro is an independent Bitcoin and macroeconomics intelligence platform. We take your privacy seriously. This policy explains what data we collect, why, and how it is used.
          </div>

          <Section title="Information We Collect">
            <p>
              We collect information only when you voluntarily provide it. Specifically:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong className="text-gray-300">Newsletter subscriptions</strong> — When you subscribe to our newsletter, your email address is collected and stored by{' '}
                <a href="https://beehiiv.com" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Beehiiv</a>,
                our email platform. We use your email solely to send the UTXOMacro newsletter. You can unsubscribe at any time via the link in any newsletter email.
              </li>
              <li>
                <strong className="text-gray-300">Contact form submissions</strong> — When you submit the contact form (name, email, and message), that data is transmitted via{' '}
                <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Resend</a>{' '}
                and routed through a Cloudflare Worker to utxomacro@gmail.com. We do <em>not</em> store your contact form data on our servers — it is sent directly as an email and not retained in any database.
              </li>
            </ul>
          </Section>

          <Section title="Cookies &amp; Tracking">
            <p>
              UTXOMacro uses <strong className="text-gray-300">Google Analytics 4 (GA4)</strong> to understand how visitors use the site — including page views, session duration, and traffic sources. GA4 uses first-party cookies to distinguish unique users. IP addresses are anonymized by default in GA4. You can opt out of Google Analytics tracking by installing the{' '}
              <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Google Analytics Opt-out Browser Add-on</a>.
            </p>
            <p>
              UTXOMacro may display advertisements served by <strong className="text-gray-300">Google AdSense</strong>. AdSense uses cookies to serve ads based on your prior visits to this site and other websites. Google's use of advertising cookies enables it and its partners to serve ads based on your browsing activity. You may opt out of personalized advertising by visiting{' '}
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Google Ads Settings</a>{' '}
              or{' '}
              <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">www.aboutads.info</a>.
            </p>
            <p>
              Some third-party services embedded on the site (such as Beehiiv newsletter widgets) may set their own cookies. We have no control over those cookies — please review their respective privacy policies for details.
            </p>
          </Section>

          <Section title="Third-Party Services">
            <p>UTXOMacro uses the following third-party services that may process your data:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>
                <strong className="text-gray-300">Beehiiv</strong> — Newsletter platform. Stores subscriber email addresses and manages send/unsubscribe flows.{' '}
                <a href="https://www.beehiiv.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Beehiiv Privacy Policy</a>
              </li>
              <li>
                <strong className="text-gray-300">Resend</strong> — Email delivery for contact form submissions. Processes message data in transit only.{' '}
                <a href="https://resend.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Resend Privacy Policy</a>
              </li>
              <li>
                <strong className="text-gray-300">Cloudflare</strong> — Infrastructure for hosting, Workers, and DDoS protection. May process request metadata.{' '}
                <a href="https://www.cloudflare.com/privacypolicy/" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Cloudflare Privacy Policy</a>
              </li>
              <li>
                <strong className="text-gray-300">Netlify</strong> — Hosting provider for the UTXOMacro website. May log request metadata.{' '}
                <a href="https://www.netlify.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Netlify Privacy Policy</a>
              </li>
              <li>
                <strong className="text-gray-300">Google Analytics 4</strong> — Used to analyze website traffic, page views, and user behavior. Processes anonymized IP addresses and first-party cookies.{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Google Privacy Policy</a>
              </li>
              <li>
                <strong className="text-gray-300">Google AdSense</strong> — Serves display advertisements and may use cookies for ad personalization.{' '}
                <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-[#F7931A] hover:underline">Google Ad Technology Policy</a>
              </li>
            </ul>
          </Section>

          <Section title="How We Use Your Information">
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>To send the UTXOMacro newsletter (email subscribers only)</li>
              <li>To respond to contact form inquiries (email only, not stored)</li>
              <li>To display relevant advertisements via Google AdSense (anonymized, cookie-based)</li>
              <li>To analyze site traffic and improve content via Google Analytics 4 (anonymized)</li>
              <li>We do not sell, rent, or share your personal information with third parties for marketing purposes</li>
            </ul>
          </Section>

          <Section title="Data Retention">
            <p>
              Newsletter subscriber emails are retained by Beehiiv until you unsubscribe. Contact form submissions are not retained — they are delivered as email and discarded in transit. We do not maintain our own database of personal information.
            </p>
          </Section>

          <Section title="Your Rights">
            <p>
              You may request access to, correction of, or deletion of any personal data we hold. Since our data processing is minimal (email newsletter only), the primary path is to unsubscribe from the newsletter via the link in any newsletter email.
            </p>
            <p>
              For any other data requests, contact us at{' '}
              <a href="mailto:utxomacro@gmail.com" className="text-[#F7931A] hover:underline">utxomacro@gmail.com</a>.
            </p>
          </Section>

          <Section title="Security">
            <p>
              We use HTTPS across the entire site. Contact form data is transmitted over encrypted connections via Resend and Cloudflare Workers. We do not store payment information (no paid features currently exist).
            </p>
          </Section>

          <Section title="Changes to This Policy">
            <p>
              We may update this Privacy Policy as the platform evolves. The "last updated" date at the top of this page will reflect any changes. Material changes will be noted in the newsletter.
            </p>
          </Section>

          <div className="mt-10 pt-8 border-t border-white/5 text-sm text-gray-600">
            Questions? Email us at{' '}
            <a href="mailto:utxomacro@gmail.com" className="text-[#F7931A] hover:underline">utxomacro@gmail.com</a>
          </div>
        </div>
      </main>

      <Footer onNavigate={onNavigate} />
    </div>
  );
};

export default Privacy;
